import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '@soap/modules';
import { ChevronDown, ChevronRight, FileText, BookOpen, PlayCircle, Printer, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../../ui/button';
import { SupportingMaterialMarkdown, type RasterFigureReviewContext } from './SupportingMaterialMarkdown';
import { renderSupportingMaterialMarkdownToHtml } from './renderSupportingMaterialMarkdown';
import { openLessonPrintWindow } from './printLessonDocument';
import { collectRasterFiguresFromSteps } from './supportingMaterialContentSegments';
import {
  stripDuplicateMaterialTitleFromContent,
  formatStepBadgeFromIds,
  stripWorksheetPreamble,
  materialStableKey,
} from './instructionStepUtils';
import {
  buildPrintHeaderHtml,
  escapeHtmlForPrint,
  getSingleMaterialPrintLayoutStyles,
  materialCardClass,
  materialTypeBadgeClass,
} from './lessonPrintLayout';
import { scheduleScrollElementIntoView } from '../LessonsPage/utils/lessonUnitNavigation';
import type {
  InstructionStepDto,
  SupportingMaterialDto,
  SupportingMaterialVideoOptionDto,
} from './types';
import { YouTubeApiServicesNotice } from '../YouTubeApiServicesNotice';
import { LessonVideoPlayer, LessonVideoRemovedPlaceholder, VIDEO_REMOVED_FROM_YOUTUBE_MESSAGE } from '../LessonVideoPlayer';
import { MediaReviewCarousel } from './MediaReviewCarousel';
import {
  getDefaultSelectedVideoId,
  isVideoRemovedFromYouTube,
  resolveStepIdForMediaCommand,
  sortVideoOptions,
  type MediaReviewSlotKind,
} from './mediaReviewUtils';

const MATERIAL_TYPE_LABELS: Record<string, string> = {
  '10': 'Worksheet',
  '20': 'Reading',
  '30': 'Video',
};

function getMaterialTypeLabel(typeKey?: string): string {
  if (!typeKey) return 'Material';
  return MATERIAL_TYPE_LABELS[typeKey] ?? typeKey;
}

function getMaterialIcon(typeKey?: string) {
  switch (typeKey) {
    case '10':
      return FileText;
    case '20':
      return BookOpen;
    case '30':
      return PlayCircle;
    default:
      return FileText;
  }
}

function formatVideoReviewIntro(optionCount: number): string {
  const optionLabel = optionCount === 1 ? 'option' : 'options';
  return `We have selected ${optionCount} ${optionLabel} for this video topic. Pick the one you want to use.`;
}

function filterVideoOptionsForDisplay(
  options: SupportingMaterialVideoOptionDto[] | undefined,
  reviewMode: boolean,
  selectedVideoId: string | null
): SupportingMaterialVideoOptionDto[] {
  const sorted = sortVideoOptions(options);
  if (reviewMode) return sorted;
  if (!selectedVideoId) return [];
  return sorted.filter(option => option.e251_VideoId === selectedVideoId);
}

function printMaterial(
  material: SupportingMaterialDto,
  lessonTitle?: string,
  instructionSteps?: InstructionStepDto[],
  selectedVideoId?: string | null,
  idToken?: string | null
) {
  const title = material.e251_Title || 'Supporting material';
  const typeLabel = getMaterialTypeLabel(material.e251_MaterialType);
  const stepBadge = formatStepBadgeFromIds(material.e251_RelatedStepIds, instructionSteps);
  const rawContent = material.e251_Content || '';
  const strippedContent =
    material.e251_MaterialType === '10' ? stripWorksheetPreamble(rawContent) : rawContent;
  const contentHtml = renderSupportingMaterialMarkdownToHtml(
    stripDuplicateMaterialTitleFromContent(strippedContent, material.e251_Title),
    collectRasterFiguresFromSteps(instructionSteps)
  );
  const keptVideoOptions = filterVideoOptionsForDisplay(
    material.e251_VideoOptions,
    false,
    selectedVideoId ?? getDefaultSelectedVideoId(material)
  );

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${escapeHtmlForPrint(title)}</title>
      <style>${getSingleMaterialPrintLayoutStyles()}</style>
    </head>
    <body>
      ${buildPrintHeaderHtml(lessonTitle || title)}
      <section class="print-section material-standalone">
        <article class="${materialCardClass(material.e251_MaterialType)}">
          <header class="material-card-header">
            <span class="${materialTypeBadgeClass(material.e251_MaterialType)}">${escapeHtmlForPrint(typeLabel)}</span>
            <h3 class="material-title">${escapeHtmlForPrint(title)}</h3>
          </header>
          <div class="material-card-body">
            ${stepBadge ? `<p class="print-meta">${escapeHtmlForPrint(stepBadge)}</p>` : ''}
            ${contentHtml}
            ${
              isVideoRemovedFromYouTube(material)
                ? `<p>${escapeHtmlForPrint(VIDEO_REMOVED_FROM_YOUTUBE_MESSAGE)}</p>`
                : keptVideoOptions.length
                ? `<p><strong>Videos:</strong></p><ol>${keptVideoOptions
                    .map(
                      option =>
                        `<li><a href="${escapeHtmlForPrint(option.e251_VideoUrl || '#')}">${escapeHtmlForPrint(option.e251_VideoTitle || 'Video')}</a>${
                          option.e251_VideoChannelTitle
                            ? ` — ${escapeHtmlForPrint(option.e251_VideoChannelTitle)}`
                            : ''
                        }</li>`
                    )
                    .join('')}</ol>`
                : ''
            }
          </div>
        </article>
      </section>
    </body>
    </html>
  `;

  void openLessonPrintWindow(html, idToken);
}

type MediaReviewSharedProps = {
  videoReviewMode?: boolean;
  getSelectedVideoId?: (materialId: string | undefined) => string | null;
  onVideoSelectionChange?: (materialId: string | undefined, videoId: string | null) => void;
  isVideoApproved?: (materialId: string | undefined) => boolean;
  isVideoApproving?: (materialId: string | undefined) => boolean;
  onApproveVideos?: (materialId: string | undefined) => void;
  isRasterApproved?: (stepId: string | undefined, figureKey: string | undefined) => boolean;
  isRasterApproving?: (stepId: string | undefined, figureKey: string | undefined) => boolean;
  onApproveRaster?: (stepId: string | undefined, figureKey: string | undefined) => void;
  onRegenerateRaster?: (
    stepId: string | undefined,
    figureKey: string | undefined,
    revisionNotes?: string
  ) => void;
  isRasterRegenerating?: (stepId: string | undefined, figureKey: string | undefined) => boolean;
  onAcceptTextOnly?: (stepId: string | undefined, figureKey: string | undefined) => void;
  isAcceptingTextOnly?: (stepId: string | undefined, figureKey: string | undefined) => boolean;
  onOpenRegenerateUnit?: () => void;
  onOpenRegenerateLesson?: () => void;
  onUploadRaster?: (
    stepId: string | undefined,
    figureKey: string | undefined,
    blobId: string,
    title?: string
  ) => void;
  isRasterUploading?: (stepId: string | undefined, figureKey: string | undefined) => boolean;
  onCheckRasterStatus?: (stepId: string | undefined, figureKey: string | undefined) => void;
  isRasterStatusChecking?: (stepId: string | undefined, figureKey: string | undefined) => boolean;
  isRasterStillGeneratingAfterCheck?: (
    stepId: string | undefined,
    figureKey: string | undefined
  ) => boolean;
  buildMediaSlotDomId?: (
    kind: MediaReviewSlotKind,
    materialId: string | undefined,
    figureKey?: string
  ) => string | undefined;
  onRegenerateMaterial?: (material: SupportingMaterialDto) => void;
  isMaterialRegenerating?: (materialId: string | undefined) => boolean;
};

function parseMaterialsToProvideList(text: string): string[] {
  const lines = text
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

  if (lines.length === 0) return [];

  const bulletItems = lines
    .map(line => line.replace(/^[-*•]\s+/, '').trim())
    .filter(Boolean);

  return bulletItems.length > 0 ? bulletItems : [text.trim()];
}

type LessonSupportingMaterialsSectionProps = MediaReviewSharedProps & {
  unitId?: string;
  lessonId?: string;
  lessonStarted?: boolean;
  materialsToProvide?: string;
  materials?: SupportingMaterialDto[];
  lessonTitle?: string;
  instructionSteps?: InstructionStepDto[];
};

function buildRasterReviewContext(
  props: MediaReviewSharedProps & {
    instructionSteps?: InstructionStepDto[];
    /** Worksheet materials (type 10) do not allow raster upload — regenerate only. */
    allowRasterUpload?: boolean;
    hostMaterialKind?: 'worksheet' | 'reading';
    onOpenRegenerateHostMaterial?: () => void;
  }
): RasterFigureReviewContext {
  const allowUpload = props.allowRasterUpload !== false;
  return {
    videoReviewMode: props.videoReviewMode,
    instructionSteps: props.instructionSteps,
    isRasterApproved: props.isRasterApproved,
    isRasterApproving: props.isRasterApproving,
    onApproveRaster: props.onApproveRaster,
    onRegenerateRaster: props.onRegenerateRaster,
    isRasterRegenerating: props.isRasterRegenerating,
    onAcceptTextOnly: props.onAcceptTextOnly,
    isAcceptingTextOnly: props.isAcceptingTextOnly,
    hostMaterialKind: props.hostMaterialKind,
    onOpenRegenerateHostMaterial: props.onOpenRegenerateHostMaterial,
    onOpenRegenerateLesson: props.onOpenRegenerateLesson,
    onOpenRegenerateUnit: props.onOpenRegenerateUnit,
    onUploadRaster: allowUpload ? props.onUploadRaster : undefined,
    isRasterUploading: allowUpload ? props.isRasterUploading : undefined,
    onCheckRasterStatus: props.onCheckRasterStatus,
    isRasterStatusChecking: props.isRasterStatusChecking,
    isRasterStillGeneratingAfterCheck: props.isRasterStillGeneratingAfterCheck,
    buildMediaSlotDomId: props.buildMediaSlotDomId,
  };
}

function MaterialCard({
  material,
  lessonTitle,
  instructionSteps,
  unitId,
  lessonId,
  lessonStarted = false,
  videoReviewMode = false,
  getSelectedVideoId,
  onVideoSelectionChange,
  isVideoApproved,
  isVideoApproving,
  onApproveVideos,
  isRasterApproved,
  isRasterApproving,
  onApproveRaster,
  onRegenerateRaster,
  isRasterRegenerating,
  onAcceptTextOnly,
  isAcceptingTextOnly,
  onOpenRegenerateUnit,
  onOpenRegenerateLesson,
  onUploadRaster,
  isRasterUploading,
  onCheckRasterStatus,
  isRasterStatusChecking,
  isRasterStillGeneratingAfterCheck,
  buildMediaSlotDomId,
  onRegenerateMaterial,
  isMaterialRegenerating,
}: MediaReviewSharedProps & {
  material: SupportingMaterialDto;
  lessonTitle?: string;
  instructionSteps?: InstructionStepDto[];
  unitId?: string;
  lessonId?: string;
  lessonStarted?: boolean;
}) {
  const { idToken } = useAuth('supporting-material-print');
  const typeKey = material.e251_MaterialType;
  const Icon = getMaterialIcon(typeKey);
  const stepBadge = formatStepBadgeFromIds(material.e251_RelatedStepIds, instructionSteps);
  const isVideo = typeKey === '30';
  const videoRemoved = isVideo && isVideoRemovedFromYouTube(material);
  const canPrint = !isVideo && Boolean(material.e251_Content?.trim());
  const hostMaterialKind: 'worksheet' | 'reading' | undefined =
    typeKey === '10' ? 'worksheet' : typeKey === '20' ? 'reading' : undefined;
  const canRegenerateMaterial =
    videoReviewMode &&
    !lessonStarted &&
    (typeKey === '10' || typeKey === '20') &&
    Boolean(onRegenerateMaterial);
  const onOpenRegenerateHostMaterial =
    canRegenerateMaterial && onRegenerateMaterial
      ? () => onRegenerateMaterial(material)
      : undefined;
  const materialId = material.e251_MaterialId;
  const materialRegenerating = isMaterialRegenerating?.(materialId) ?? false;
  const approved = isVideoApproved?.(materialId) ?? material.e251_VideoApproved === true;
  const approving = isVideoApproving?.(materialId) ?? false;
  const showApproval = isVideo && videoReviewMode && !videoRemoved;
  const needsApproval = showApproval && !approved && !approving;
  const videoCardDomId = isVideo ? buildMediaSlotDomId?.('video', materialId) : undefined;
  const cardClassName = needsApproval ? 'bg-[#D4616B]/5 py-2.5' : 'py-2.5';

  const selectedVideoId = videoReviewMode
    ? (getSelectedVideoId?.(materialId) ?? null)
    : (getSelectedVideoId?.(materialId) ?? getDefaultSelectedVideoId(material));

  const displayVideoOptions = filterVideoOptionsForDisplay(
    material.e251_VideoOptions,
    videoReviewMode,
    selectedVideoId
  );

  const carouselOptions = sortVideoOptions(material.e251_VideoOptions)
    .filter(option => option.e251_VideoId)
    .map(option => ({
      id: option.e251_VideoId as string,
      label: option.e251_VideoTitle,
      thumbnailUrl: option.e251_VideoId
        ? `https://img.youtube.com/vi/${option.e251_VideoId}/mqdefault.jpg`
        : undefined,
      rank: option.e251_VideoOptionRank,
    }));

  const hasSelectedVideo = Boolean(selectedVideoId);

  const displayContent = stripDuplicateMaterialTitleFromContent(
    material.e251_Content || '',
    material.e251_Title
  );
  const [liveContent, setLiveContent] = React.useState(displayContent);

  React.useEffect(() => {
    setLiveContent(displayContent);
  }, [displayContent]);

  const figureRegenerateContext =
    unitId && lessonId && materialId && !lessonStarted
      ? {
          unitId,
          lessonId,
          materialId,
          onMaterialContentUpdated: setLiveContent,
          onAcceptTextOnly: onAcceptTextOnly
            ? (figureKey: string) =>
                onAcceptTextOnly(resolveStepIdForMediaCommand(materialId) ?? materialId, figureKey)
            : undefined,
          isAcceptingTextOnly: onAcceptTextOnly
            ? (figureKey: string) =>
                isAcceptingTextOnly?.(
                  resolveStepIdForMediaCommand(materialId) ?? materialId,
                  figureKey
                ) ?? false
            : undefined,
          hostMaterialKind,
          onOpenRegenerateHostMaterial,
          onOpenRegenerateLesson,
          onOpenRegenerateUnit,
        }
      : undefined;

  const rasterReview = buildRasterReviewContext({
    videoReviewMode,
    instructionSteps,
    isRasterApproved,
    isRasterApproving,
    onApproveRaster,
    onRegenerateRaster,
    isRasterRegenerating,
    onAcceptTextOnly,
    isAcceptingTextOnly,
    hostMaterialKind,
    onOpenRegenerateHostMaterial,
    onOpenRegenerateLesson,
    onOpenRegenerateUnit,
    onUploadRaster,
    isRasterUploading,
    onCheckRasterStatus,
    isRasterStatusChecking,
    isRasterStillGeneratingAfterCheck,
    buildMediaSlotDomId,
    // Worksheet diagrams: regenerate only — no teacher upload replacement.
    allowRasterUpload: typeKey !== '10',
  });

  return (
    <div id={videoCardDomId} className={cardClassName}>
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2">
          <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#1a5172]" aria-hidden />
          <div className="min-w-0">
            <h6 className="text-sm font-semibold text-gray-900">
              {material.e251_Title || 'Supporting material'}
            </h6>
            {stepBadge && <p className="mt-0.5 text-xs text-gray-500">{stepBadge}</p>}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {needsApproval && (
            <span className="inline-flex items-center rounded-full bg-[#D4616B]/10 px-2 py-0.5 text-xs font-semibold text-[#BE5059]">
              Needs approval
            </span>
          )}
          {showApproval && approved && (
            <span className="inline-flex items-center rounded-full bg-[#f0fdf4] px-2 py-0.5 text-xs font-semibold text-[#15803d]">
              Approved
            </span>
          )}
          {canRegenerateMaterial && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 shrink-0 gap-1 text-xs text-action-blue border-action-blue/30 hover:bg-action-blue/10"
              disabled={materialRegenerating}
              title={`Regenerate this ${typeKey === '10' ? 'worksheet' : 'reading'}`}
              onClick={() => onRegenerateMaterial?.(material)}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${materialRegenerating ? 'animate-spin' : ''}`} />
              {materialRegenerating
                ? 'Regenerating…'
                : typeKey === '10'
                  ? 'Regenerate Worksheet'
                  : 'Regenerate Reading'}
            </Button>
          )}
          {canPrint && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 shrink-0 gap-1 text-xs"
              onClick={() =>
                printMaterial(material, lessonTitle, instructionSteps, selectedVideoId, idToken)
              }
            >
              <Printer className="h-3.5 w-3.5" />
              Print
            </Button>
          )}
        </div>
      </div>

      {isVideo ? (
        <div className="space-y-2">
          {liveContent && (
            <SupportingMaterialMarkdown
              content={liveContent}
              figureRegenerateContext={figureRegenerateContext}
              rasterReview={rasterReview}
            />
          )}
          {videoRemoved ? (
            <LessonVideoRemovedPlaceholder />
          ) : displayVideoOptions.length > 0 || (videoReviewMode && carouselOptions.length > 0) ? (
            <div className={videoReviewMode ? 'space-y-4' : 'space-y-2'}>
              {videoReviewMode ? (
                <MediaReviewCarousel
                  options={carouselOptions}
                  selectedOptionId={selectedVideoId}
                  onSelectOption={videoId => onVideoSelectionChange?.(materialId, videoId)}
                  introText={formatVideoReviewIntro(carouselOptions.length)}
                  ariaLabel="Video review carousel"
                  renderPreview={option => {
                    const videoOption = material.e251_VideoOptions?.find(
                      candidate => candidate.e251_VideoId === option.id
                    );
                    return (
                      <div className="flex flex-col items-center p-3 text-center">
                        <div className="w-full max-w-md">
                          <LessonVideoPlayer
                            videoId={videoOption?.e251_VideoId}
                            title={videoOption?.e251_VideoTitle || option.label || 'Video option'}
                            channelTitle={videoOption?.e251_VideoChannelTitle}
                            url={videoOption?.e251_VideoUrl}
                            embeddable={videoOption?.e251_VideoEmbeddable}
                          />
                        </div>
                      </div>
                    );
                  }}
                />
              ) : (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Videos</p>
                  <ol className="list-decimal space-y-4 pl-5 text-sm">
                    {displayVideoOptions.map((option, index) => (
                      <li
                        key={option.e251_VideoId || `${index}-${option.e251_VideoUrl}`}
                        className="text-gray-800"
                      >
                        <LessonVideoPlayer
                          videoId={option.e251_VideoId}
                          title={option.e251_VideoTitle || `Option ${index + 1}`}
                          channelTitle={option.e251_VideoChannelTitle}
                          url={option.e251_VideoUrl}
                          embeddable={option.e251_VideoEmbeddable}
                        />
                      </li>
                    ))}
                  </ol>
                </>
              )}
              <YouTubeApiServicesNotice />
            </div>
          ) : material.e251_Url ? (
            <div className="space-y-2">
              <LessonVideoPlayer
                title={material.e251_Title || 'Video'}
                channelTitle={material.e251_VideoChannelTitle}
                url={material.e251_Url}
              />
              <YouTubeApiServicesNotice />
            </div>
          ) : null}
          {showApproval && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {approving ? (
                <span className="inline-flex items-center gap-2 text-xs text-gray-600">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  Saving your video approval…
                </span>
              ) : needsApproval ? (
                <>
                  <Button
                    type="button"
                    size="sm"
                    className="h-8 gap-1 bg-[#16a34a] text-xs text-white hover:bg-[#15803d]"
                    disabled={!hasSelectedVideo}
                    onClick={() => onApproveVideos?.(materialId)}
                  >
                    Approve video
                  </Button>
                  <span className="text-xs text-[#BE5059]">
                    Review the suggested video, then approve to confirm your choice.
                  </span>
                </>
              ) : (
                <span className="text-xs text-gray-500">One video included for this lesson.</span>
              )}
            </div>
          )}
        </div>
      ) : (
        liveContent && (
          <SupportingMaterialMarkdown
            content={liveContent}
            figureRegenerateContext={figureRegenerateContext}
            rasterReview={rasterReview}
          />
        )
      )}
    </div>
  );
}

function MaterialGroup({
  title,
  materials,
  lessonTitle,
  instructionSteps,
  unitId,
  lessonId,
  lessonStarted = false,
  defaultExpanded = true,
  videoReviewMode,
  getSelectedVideoId,
  onVideoSelectionChange,
  isVideoApproved,
  isVideoApproving,
  onApproveVideos,
  isRasterApproved,
  isRasterApproving,
  onApproveRaster,
  onRegenerateRaster,
  isRasterRegenerating,
  onAcceptTextOnly,
  isAcceptingTextOnly,
  onOpenRegenerateUnit,
  onOpenRegenerateLesson,
  onUploadRaster,
  isRasterUploading,
  onCheckRasterStatus,
  isRasterStatusChecking,
  isRasterStillGeneratingAfterCheck,
  buildMediaSlotDomId,
  onRegenerateMaterial,
  isMaterialRegenerating,
  headerCount,
}: MediaReviewSharedProps & {
  title: string;
  materials: SupportingMaterialDto[];
  lessonTitle?: string;
  instructionSteps?: InstructionStepDto[];
  unitId?: string;
  lessonId?: string;
  lessonStarted?: boolean;
  defaultExpanded?: boolean;
  headerCount?: number;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const rootRef = useRef<HTMLDivElement>(null);
  const wasExpandedRef = useRef(expanded);

  useEffect(() => {
    const justExpanded = expanded && !wasExpandedRef.current;
    wasExpandedRef.current = expanded;
    if (!justExpanded) return;

    const handle = scheduleScrollElementIntoView(() => rootRef.current, { delayMs: 120 });
    return () => window.clearTimeout(handle);
  }, [expanded]);

  if (!materials.length) return null;

  return (
    <div
      ref={rootRef}
      className="overflow-hidden rounded-md border border-gray-200 bg-gray-50/80"
    >
      <button
        type="button"
        onClick={() => setExpanded(prev => !prev)}
        className={`flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blumine focus-visible:ring-offset-2 ${
          expanded ? 'border-b border-gray-200' : ''
        }`}
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-600" aria-hidden />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-600" aria-hidden />
        )}
        <h6 className="text-xs font-semibold uppercase tracking-wide text-gray-700">
          {title}
          <span className="ml-1.5 font-normal normal-case text-gray-500">
            ({headerCount ?? materials.length})
          </span>
        </h6>
      </button>
      {expanded && (
        <div className="divide-y divide-gray-100 bg-white px-2.5 py-1">
          {materials.map((material, index) => (
            <MaterialCard
              key={materialStableKey(material, index)}
              material={material}
              lessonTitle={lessonTitle}
              instructionSteps={instructionSteps}
              unitId={unitId}
              lessonId={lessonId}
              lessonStarted={lessonStarted}
              videoReviewMode={videoReviewMode}
              getSelectedVideoId={getSelectedVideoId}
              onVideoSelectionChange={onVideoSelectionChange}
              isVideoApproved={isVideoApproved}
              isVideoApproving={isVideoApproving}
              onApproveVideos={onApproveVideos}
              isRasterApproved={isRasterApproved}
              isRasterApproving={isRasterApproving}
              onApproveRaster={onApproveRaster}
              onRegenerateRaster={onRegenerateRaster}
              isRasterRegenerating={isRasterRegenerating}
              onAcceptTextOnly={onAcceptTextOnly}
              isAcceptingTextOnly={isAcceptingTextOnly}
              onOpenRegenerateUnit={onOpenRegenerateUnit}
              onOpenRegenerateLesson={onOpenRegenerateLesson}
              onUploadRaster={onUploadRaster}
              isRasterUploading={isRasterUploading}
              onCheckRasterStatus={onCheckRasterStatus}
              isRasterStatusChecking={isRasterStatusChecking}
              isRasterStillGeneratingAfterCheck={isRasterStillGeneratingAfterCheck}
              buildMediaSlotDomId={buildMediaSlotDomId}
              onRegenerateMaterial={onRegenerateMaterial}
              isMaterialRegenerating={isMaterialRegenerating}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function LessonSupportingMaterialsSection({
  materialsToProvide,
  materials,
  lessonTitle,
  instructionSteps,
  unitId,
  lessonId,
  lessonStarted = false,
  videoReviewMode = false,
  getSelectedVideoId,
  onVideoSelectionChange,
  isVideoApproved,
  isVideoApproving,
  onApproveVideos,
  isRasterApproved,
  isRasterApproving,
  onApproveRaster,
  onRegenerateRaster,
  isRasterRegenerating,
  onAcceptTextOnly,
  isAcceptingTextOnly,
  onOpenRegenerateUnit,
  onOpenRegenerateLesson,
  onUploadRaster,
  isRasterUploading,
  onCheckRasterStatus,
  isRasterStatusChecking,
  isRasterStillGeneratingAfterCheck,
  buildMediaSlotDomId,
  onRegenerateMaterial,
  isMaterialRegenerating,
}: LessonSupportingMaterialsSectionProps) {
  const supplyItems = materialsToProvide?.trim()
    ? parseMaterialsToProvideList(materialsToProvide)
    : [];
  const sorted = [...(materials ?? [])].sort(
    (a, b) => (a.e251_SortOrder ?? 0) - (b.e251_SortOrder ?? 0)
  );
  const worksheets = sorted.filter(m => m.e251_MaterialType === '10');
  const readings = sorted.filter(m => m.e251_MaterialType === '20');
  const videos = sorted.filter(m => m.e251_MaterialType === '30');
  const videoHeaderCount = videos.length;
  const hasProvided = worksheets.length > 0 || readings.length > 0 || videos.length > 0;
  const hasInlineRasterSlots = (instructionSteps ?? []).some(
    step => (step.e251_RasterFigures?.length ?? 0) > 0
  );
  const expandMaterialsWithImages = videoReviewMode && hasInlineRasterSlots;

  if (!supplyItems.length && !hasProvided) return null;

  const sharedRasterProps = {
    videoReviewMode,
    isRasterApproved,
    isRasterApproving,
    onApproveRaster,
    onRegenerateRaster,
    isRasterRegenerating,
    onAcceptTextOnly,
    isAcceptingTextOnly,
    onOpenRegenerateUnit,
    onOpenRegenerateLesson,
    onUploadRaster,
    isRasterUploading,
    onCheckRasterStatus,
    isRasterStatusChecking,
    isRasterStillGeneratingAfterCheck,
    buildMediaSlotDomId,
    onRegenerateMaterial,
    isMaterialRegenerating,
  };

  return (
    <div className="space-y-3">
      <h6 className="text-xs font-semibold uppercase tracking-wide text-gray-700">Materials</h6>
      {supplyItems.length > 0 && (
        <ul className="list-disc space-y-1 pl-5 text-sm text-gray-600">
          {supplyItems.map((item, index) => (
            <li key={`${index}-${item}`}>{item}</li>
          ))}
        </ul>
      )}
      {hasProvided && (
        <div className="space-y-3">
          <MaterialGroup
            title="Worksheets"
            materials={worksheets}
            lessonTitle={lessonTitle}
            instructionSteps={instructionSteps}
            unitId={unitId}
            lessonId={lessonId}
            lessonStarted={lessonStarted}
            defaultExpanded={expandMaterialsWithImages}
            {...sharedRasterProps}
          />
          <MaterialGroup
            title="Reading"
            materials={readings}
            lessonTitle={lessonTitle}
            instructionSteps={instructionSteps}
            unitId={unitId}
            lessonId={lessonId}
            lessonStarted={lessonStarted}
            defaultExpanded={expandMaterialsWithImages}
            {...sharedRasterProps}
          />
          <MaterialGroup
            title="Videos"
            materials={videos}
            lessonTitle={lessonTitle}
            instructionSteps={instructionSteps}
            unitId={unitId}
            lessonId={lessonId}
            lessonStarted={lessonStarted}
            headerCount={videoHeaderCount}
            videoReviewMode={videoReviewMode}
            getSelectedVideoId={getSelectedVideoId}
            onVideoSelectionChange={onVideoSelectionChange}
            isVideoApproved={isVideoApproved}
            isVideoApproving={isVideoApproving}
            onApproveVideos={onApproveVideos}
            {...sharedRasterProps}
          />
        </div>
      )}
    </div>
  );
}
